import { useEffect, useState } from 'react';

/**
 * The editing window every order opens (`closes_at`, one hour after it is placed by default):
 * until then the customer may still edit or cancel it; after it the order is closed and billed by
 * boleto. The API decides `editable`; this file only counts down to `closes_at` so the screen can
 * say "fecha em 38 min" and switch to "fechado" the moment it happens, without a refetch.
 */

const TIME = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
});

/** `15:42` in São Paulo. */
export function formatClosingTime(closesAt: string): string {
  const at = Date.parse(closesAt);
  return Number.isFinite(at) ? TIME.format(new Date(at)) : '';
}

/** `38 min`, `1 h 05 min`, or `45 s` in the last minute. */
export function formatTimeLeft(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  if (seconds < 60) return `${String(seconds)} s`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${String(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  return `${String(hours)} h ${String(minutes % 60).padStart(2, '0')} min`;
}

/**
 * Milliseconds until `closesAt`, ticking: once a second in the last minute, every fifteen
 * seconds before that. Stops at zero.
 */
export function useTimeLeft(closesAt: string | null | undefined): number {
  const end = closesAt == null ? Number.NaN : Date.parse(closesAt);
  const [now, setNow] = useState(() => Date.now());
  const left = Number.isFinite(end) ? end - now : 0;

  useEffect(() => {
    if (left <= 0) return undefined;
    const timer = window.setTimeout(
      () => {
        setNow(Date.now());
      },
      left <= 60_000 ? 1_000 : Math.min(15_000, left - 59_000),
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [left]);

  return Math.max(0, left);
}
